import React, { useState, useEffect, useRef, useCallback } from 'react';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import { contactService } from '../../services/contactService.js';
import { Plus, Mail } from "lucide-react";
import ListFilterPanel from '../common/ListFilterPanel.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import ListPagination from '../common/ListPagination.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import { useTranslation } from 'react-i18next';
import { listIdLinkClass, listIdLinkMobileClass } from '../common/uiClasses.js';


const ContactList = () => {
  const { t } = useTranslation();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const perPage = 10;

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (search) filters.search = search;
      if (filter) filters.replied = filter;

      const response = await contactService.getAllContacts(currentPage, perPage, filters);
      setContacts((prev) => mergeLoadedPage(currentPage, response.data.data || [], prev));
      setTotalPages(response.data.pagination.pages);
    } catch (err) {
      Alert.error('Failed to load contacts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, filter, currentPage, perPage]);

  usePaginatedListFetch({
    fetchPage: fetchContacts,
    setCurrentPage,
    currentPage,
    resetPageDeps: [search, filter],
    debounceMs: 0,
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDelete = async (contactId, contactName) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete the contact from "${contactName}"? This action cannot be undone.`,
      'Delete Contact'
    );
    
    if (confirmed) {
      try {
        await contactService.deleteContact(contactId);
        Alert.success('Contact deleted successfully!');
        
        if (contacts.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchContacts();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete contact');
      }
    }
  };

  const toggleDropdown = (contactId) => {
    setActiveDropdown(activeDropdown === contactId ? null : contactId);
  };

  const clearFilters = () => {
    setSearch('');
    setFilter('');
    setCurrentPage(1);
  };

  const hasActiveFilters = search || filter;

  return (
    <DashboardLayout>
                <ListPageHeader
                  title="Contact Messages"
                  icon={Mail}
                  titleClassName="text-secondary"
                  iconClassName="w-6 h-6 sm:w-7 sm:h-7 text-buttonBg shrink-0"
                  className="mb-4 sm:mb-6"
                />

                <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters}>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">{t('common.search')}</label>
                    <SearchSubmitInput
                      value={search}
                      onSearch={setSearch}
                      placeholder="Name or email..."
                      inputClassName="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Status</label>
                    <select
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                      <option value="">All Messages</option>
                      <option value="yes">Replied</option>
                      <option value="no">Pending</option>
                    </select>
                  </div>
                </ListFilterPanel>

                {/* Table */}
                <div className="bg-white rounded-xl shadow-sm">
                  {loading && currentPage === 1 ? (
                    <div className="p-6 sm:p-8 text-center text-gray-600">
                      <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                      <p className="text-sm sm:text-base">Loading contacts...</p>
                    </div>
                  ) : contacts.length === 0 ? (
                    <div className="p-6 sm:p-8 text-center text-sm sm:text-base text-gray-600">No contacts found</div>
                  ) : (
                    <>
                      {/* Desktop Table View */}
                      <div className="hidden lg:block w-full" onClick={() => setActiveDropdown(null)}>
                        <table className="w-full table-fixed border-collapse">
                          <thead>
                            <tr className="bg-primary text-tertiary">
                              <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-semibold w-[16%]">CUSTOMER NAME</th>
                              <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-semibold w-[18%]">EMAIL</th>
                              <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-semibold">PHONE</th>
                              <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-semibold w-[20%]">SUBJECT</th>
                              <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-semibold">STATUS</th>
                              <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-semibold">DATE</th>
                              <th className="px-4 xl:px-6 py-3 xl:py-4 text-left text-xs xl:text-sm font-semibold">ACTIONS</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {contacts.map((contact) => (
                              <tr key={contact.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 xl:px-6 py-3 xl:py-4">
                                  <button
                                    onClick={() => navigate(`/contact/view/${contact.id}`)}
                                    className={listIdLinkClass}
                                  >
                                    {contact.full_name}
                                  </button>
                                </td>
                                <td className="px-4 xl:px-6 py-3 xl:py-4 text-gray-700 text-sm xl:text-base">{contact.email}</td>
                                <td className="px-4 xl:px-6 py-3 xl:py-4 text-gray-700 text-sm xl:text-base">{contact.phone}</td>
                                <td className="px-4 xl:px-6 py-3 xl:py-4 text-gray-700 text-sm xl:text-base max-w-xs truncate">{contact.subject}</td>
                                <td className="px-4 xl:px-6 py-3 xl:py-4">
                                  <span className={`px-2 xl:px-3 py-1 rounded-full text-xs font-semibold ${
                                    contact.reply
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {contact.reply ? 'Replied' : 'Pending'}
                                  </span>
                                </td>
                                <td className="px-4 xl:px-6 py-3 xl:py-4 text-gray-700 text-sm xl:text-base">
                                  {new Date(contact.created_at).toLocaleDateString('en-IN', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                  })}
                                </td>
                                <td className="px-4 xl:px-6 py-3 xl:py-4 relative">
                                  <RowActionsMenu
                                    isOpen={activeDropdown === contact.id}
                                    onToggle={() => toggleDropdown(contact.id)}
                                    onClose={() => setActiveDropdown(null)}
                                    menuRef={dropdownRef}
                                    onView={() => navigate(`/contact/view/${contact.id}`)}
                                    onEdit={() => navigate(`/contact/edit/${contact.id}`)}
                                    editLabel={contact.reply ? 'View Reply' : 'Reply'}
                                    onDelete={() => handleDelete(contact.id, contact.full_name)}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile/Tablet Card View */}
                      <div className="lg:hidden divide-y divide-gray-200" onClick={() => setActiveDropdown(null)}>
                        {contacts.map((contact) => (
                          <div key={contact.id} className="p-4 hover:bg-gray-50 transition-colors">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1 min-w-0 mr-2">
                                <button
                                  onClick={() => navigate(`/contact/view/${contact.id}`)}
                                  className={`${listIdLinkMobileClass} block truncate`}
                                >
                                  {contact.full_name}
                                </button>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                    contact.reply
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {contact.reply ? 'Replied' : 'Pending'}
                                  </span>
                                </div>
                              </div>
                              <RowActionsMenu
                                isOpen={activeDropdown === contact.id}
                                onToggle={() => toggleDropdown(contact.id)}
                                onClose={() => setActiveDropdown(null)}
                                menuRef={dropdownRef}
                                onView={() => navigate(`/contact/view/${contact.id}`)}
                                onEdit={() => navigate(`/contact/edit/${contact.id}`)}
                                editLabel={contact.reply ? 'View Reply' : 'Reply'}
                                onDelete={() => handleDelete(contact.id, contact.full_name)}
                                className="relative flex-shrink-0"
                              />
                            </div>
                            
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-500">Email:</span>
                                <span className="text-gray-900 font-medium truncate ml-2">{contact.email}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Phone:</span>
                                <span className="text-gray-900 font-medium">{contact.phone}</span>
                              </div>
                              <div className="flex flex-col">
                                <span className="text-gray-500 mb-1">Subject:</span>
                                <span className="text-gray-900 font-medium line-clamp-2">{contact.subject}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Date:</span>
                                <span className="text-gray-900 font-medium">
                                  {new Date(contact.created_at).toLocaleDateString('en-IN', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                  })}
                                </span>
                              </div>
                            </div>

                          </div>
                        ))}
                      </div>

                      {/* Pagination */}
                      <ListPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        loading={loading}
                        onNext={() => setCurrentPage((page) => page + 1)}
                      />
                    </>
                  )}
                </div>
    </DashboardLayout>
  );
};

export default ContactList;